import { useMemo } from 'react';

import { Badge, BadgeType, FlexAligner, LoadingBubbles, Pulse, Typography } from '@scaleaq/scaleui';
import { BargeControlAlarmState } from '@scaleaq/sitecontrol-lib';
import { radius, spacing } from '@scaleaq/sui-tokens/responsive';
import { useTranslation } from 'react-i18next';

import { Toolbar } from '@/components';
import { Z_INDEX } from '@/constants';
import { colors, Icon, responsive } from '@/utils';

import { getBadgeType, getToolbarActionList } from './alarmBadge.functions.ts';
import type { AlarmBadgeProps } from './alarmBadge.types.ts';

/**
 * AlarmBadge component displays a badge with an alarm state and an optional toolbar for actions.
 * @param alarmState The state of the alarm, default is Healthy.
 * @param description Optional description of the alarm. Will wrap the alarm with a border.
 * @param isAckDisabled will disable the acknowledge button. e.g. when isLoading === true
 * @param label
 * @param menuLeftOrRight default is left
 * @param onAcknowledge
 * @param showHealthyStatus Allow to show the healthy status. Default is false.
 * @param spaceBetweenBadgeAndToolbar If false, toolbar shows close to badge
 * @constructor
 */
export const AlarmBadge = ({
  alarmState = BargeControlAlarmState.Healthy,
  description = '',
  isAckDisabled = false,
  label,
  menuLeftOrRight = 'left',
  onAcknowledge,
  showHealthyStatus = false,
  spaceBetweenBadgeAndToolbar = true,
}: AlarmBadgeProps) => {
  const { t } = useTranslation(['bargeControl', 'common']);

  const menuPosition = useMemo(
    () => (menuLeftOrRight === 'left' ? 'bottom-end' : 'bottom-start'),
    [menuLeftOrRight]
  );
  const badgeType: BadgeType | undefined = useMemo(
    () => getBadgeType(alarmState, showHealthyStatus),
    [alarmState, showHealthyStatus]
  );
  const isPulsing = useMemo(
    () => alarmState === BargeControlAlarmState.UnacknowlegeActiveAlarm,
    [alarmState]
  );
  const toolbarActionList = useMemo(
    () => getToolbarActionList(alarmState, t('alarm.toAck.ack'), onAcknowledge),
    [alarmState, onAcknowledge, t]
  );
  const hasDescription = useMemo(
    () => description !== undefined && description.length > 0,
    [description]
  );
  const style = useMemo(
    () =>
      hasDescription
        ? {
            border: `1px solid ${colors.stroke.neutral['50']}`,
            borderRadius: `${radius['2xlarge']}`,
            padding: `${spacing['xlarge']}`,
          }
        : {},
    [hasDescription]
  );

  if (badgeType === undefined) {
    return null;
  }

  return (
    <FlexAligner style={style} direction='column'>
      {hasDescription && <Typography>{description}</Typography>}
      <FlexAligner alignment={'center-left'} spaceBetween={spaceBetweenBadgeAndToolbar}>
        <Pulse $pulse={isPulsing} $behavior={'DecayAttack'}>
          <Badge style={{ marginTop: '2px' }} key={1} label={label} type={badgeType} />
        </Pulse>
        {toolbarActionList.length > 0 ? (
          <Toolbar
            style={{ minHeight: `${spacing['4xlarge']}` }}
            Icon={isAckDisabled ? <LoadingBubbles /> : <Icon.Ellipsis size='large' />}
            actionList={toolbarActionList}
            menuPlacement={menuPosition}
            disabled={isAckDisabled}
            zIndex={Z_INDEX.content.floatingTooltip}
          />
        ) : (
          <div style={{ paddingLeft: responsive.spacing.medium, width: '32px', height: '24px' }}>
            <Icon.Ellipsis
              size='large'
              style={{
                stroke: colors.icon.disabled,
                color: colors.icon.disabled,
              }}
            />
          </div>
        )}
      </FlexAligner>
    </FlexAligner>
  );
};
